import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { SectionKey } from 'types';

export type SetSearchParams = (search: URLSearchParams) => void;

type ValidateSearchParamsParams = {
  sectionOrder: SectionKey[];
  sectionData: PvWizardSections;
  searchState: string;
};

const validateSearchParams = ({
  sectionData,
  sectionOrder,
  searchState
}: ValidateSearchParamsParams) => {
  const initialParams = Object.fromEntries(new URLSearchParams(searchState).entries());
  const paramKeys = Object.keys(initialParams);

  const wizardQuestionsAnswerMap = sectionOrder
    .map(section =>
      sectionData[section].questions
        .map(question =>
          question.__typename === 'PvWizardMultiQuestionType'
            ? [
                {
                  key: question.questions.roofAge.key,
                  answers: question.questions.roofAge.answerOptions.map(option => option.value)
                },
                {
                  key: question.questions.roofMaterial.key,
                  answers: question.questions.roofMaterial.answerOptions.map(option => option.value)
                }
              ]
            : {
                key: question.key,
                answers: question.answerOptions.map(option => option.value)
              }
        )
        .flat()
    )
    .flat();

  const hasInvalidParam = paramKeys.filter(key => {
    const possibleAnswers = wizardQuestionsAnswerMap.find(
      question => question.key === key
    )?.answers;

    const hasFalseAnswer = !possibleAnswers?.find(answer => answer === initialParams[key]);

    return Boolean(hasFalseAnswer);
  });

  return {
    hasValidParam: !Boolean(hasInvalidParam?.length)
  };
};

export const useSearchParams = (
  sectionOrder: SectionKey[],
  sectionData: PvWizardSections
): [URLSearchParams, SetSearchParams] => {
  const navigate = useNavigate();
  const { search, pathname } = useLocation();
  const [searchState, setSearchState] = useState<string>(search);

  const setSearchParams: SetSearchParams = nextSearchParams => {
    const newSearchString = nextSearchParams.toString();
    setSearchState(newSearchString ? `?${nextSearchParams.toString()}` : '');
  };

  // intial validation
  useEffect(() => {
    if (searchState) {
      const { hasValidParam } = validateSearchParams({
        sectionOrder,
        sectionData,
        searchState
      });

      if (!hasValidParam) {
        setSearchState('');
      }
    }
  }, []);

  useEffect(() => {
    if (searchState !== search) {
      setSearchState(search);
    }
  }, [search]);

  useEffect(() => {
    if (search !== searchState) {
      navigate({ pathname: pathname, search: searchState }, { preventScrollReset: true });
    }
  }, [searchState]);

  return [new URLSearchParams(searchState), setSearchParams];
};
